import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { 
  SpeakerWaveIcon, 
  CurrencyDollarIcon, 
  ShoppingCartIcon, 
  UsersIcon,
  ChartBarIcon,
  UserGroupIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { jwtDecode } from "jwt-decode";
import reportsAPI from '../api/reportsAPI';
import { formatNumber } from '../utils/numberUtils';
import SalesChart from '../components/SalesChart';
import AccountingFirmDashboard from '../components/AccountingFirmDashboard';
import TaxDashboard from '../components/TaxDashboard';
import { isAccountingClient } from '../utils/tenantType';
import RestaurantDashboard from './RestaurantDashboard';

const ClientDashboardPage = () => {
    const { isAuthenticated, isAuthReady, getAccessToken, user } = useAuth();
    const navigate = useNavigate();
    const [role, setRole] = useState('cashier');

    // billing_ff users have no dashboard — send them straight to their module
    useEffect(() => {
        if (user?.role === 'billing_ff') navigate('/billing/ff-billing', { replace: true });
    }, [user]);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('today');
    const [tenantInfo, setTenantInfo] = useState(null);
    const [tenantLoading, setTenantLoading] = useState(true);

    // Extract role from JWT token
    useEffect(() => {
        if (!isAuthReady) return;
        const token = getAccessToken();
        if (!isAuthenticated || !token) return;
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setRole(decoded.role || 'cashier');
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        }
    }, [isAuthReady, isAuthenticated, getAccessToken]);

    // Fetch tenant info for routing
    useEffect(() => {
        const fetchTenantInfo = async () => {
            if (!isAuthReady) return;
            if (!isAuthenticated || !getAccessToken()) return;

            try {
                setTenantLoading(true);
                const res = await api.get('/me');
                
                //console.log('Tenant Info Response:', res.data);
                setTenantInfo(res.data.tenant);
            } catch (err) {
                console.error('Error fetching tenant info:', err);
            } finally {
                setTenantLoading(false);
            }
        };

        fetchTenantInfo();
    }, [isAuthReady, isAuthenticated, getAccessToken]);

    // Fetch dashboard data
    useEffect(() => {
        if (!isAuthReady || !isAuthenticated) return;
        fetchDashboardData();
    }, [isAuthReady, isAuthenticated, timeRange]);

    const fetchDashboardData = async () => {
        if (!isAuthenticated || !getAccessToken()) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await reportsAPI.getDashboardData(timeRange);
            
            // Check if response has data
            if (response.data && response.data.success) {
                setDashboardData(response.data.data);
            } else {
                throw new Error(response.data?.message || 'Invalid response format');
            }
        } catch (err) {
            setError(err.message || 'Failed to load dashboard data');
            console.error('Dashboard data error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Safe data access with defaults
    const getSafeData = () => {
        if (!dashboardData) {
            return {
                sales: { current: 0, previous: 0 },
                orders: { current: 0, previous: 0 },
                averageOrderValue: 0,
                previousAverageOrderValue: 0,
                topProduct: null,
                userSales: { today: 0, transactions: 0 },
                inventory: { lowStock: 0 },
                users: { active: 0 },
                stores: { active: 0 },
                recentActivity: []
            };
        }
        return dashboardData;
    };

    // Calculate trends with safe defaults
    const calculateTrend = (current, previous) => {
        if (!current && !previous) return { value: 0, isPositive: true };
        if (!previous || previous === 0) return { value: 100, isPositive: true };
        
        const change = ((current - previous) / previous) * 100;
        return {
            value: Math.abs(change).toFixed(1),
            isPositive: change >= 0
        };
    };

    // Common widgets for all roles with safe data access
    const getCommonWidgets = () => {
        const data = getSafeData();
        
        const salesTrend = calculateTrend(
            data.sales?.current || 0,
            data.sales?.previous || 0
        );

        const ordersTrend = calculateTrend(
            data.orders?.current || 0,
            data.orders?.previous || 0
        );

        const avgOrderValue = data.averageOrderValue || 0;
        const prevAvgOrderValue = data.previousAverageOrderValue || 0;
        const avgOrderTrend = calculateTrend(avgOrderValue, prevAvgOrderValue);

        return [
            {
                title: "Total Sales",
                value: `₱${formatNumber(data.sales?.current || 0)}`,
                icon: <CurrencyDollarIcon className="h-10 w-10 text-green-600 mr-4" />,
                description: `${timeRange} sales`,
                trend: salesTrend,
                previousValue: `₱${formatNumber(data.sales?.previous || 0)}`
            },
            {
                title: "Total Orders",
                value: (data.orders?.current || 0).toString(),
                icon: <ShoppingCartIcon className="h-10 w-10 text-blue-600 mr-4" />,
                description: `${timeRange} orders`,
                trend: ordersTrend,
                previousValue: (data.orders?.previous || 0).toString()
            },
            {
                title: "Average Order Value",
                value: `₱${formatNumber(avgOrderValue)}`,
                icon: <ChartBarIcon className="h-10 w-10 text-purple-600 mr-4" />,
                description: "Per order average",
                trend: avgOrderTrend
            },
            {
                title: "Top Product",
                value: data.topProduct?.name || "No data",
                icon: <UserGroupIcon className="h-10 w-10 text-orange-600 mr-4" />,
                description: data.topProduct ? `${data.topProduct.quantity || 0} sold` : "No sales data",
                isProduct: true
            }
        ];
    };

    // Role-specific dashboard configuration with safe data access
    const getRoleConfig = () => {
        const data = getSafeData();
        
        const baseConfig = {
            superadmin: {
                widgets: [
                    {
                        title: "Active Stores",
                        value: (data.stores?.active || 0).toString(),
                        icon: <ShoppingCartIcon className="h-10 w-10 text-indigo-600 mr-4" />,
                        description: "Currently active stores"
                    },
                    {
                        title: "System Health",
                        value: "98%",
                        icon: <CogIcon className="h-10 w-10 text-yellow-600 mr-4" />,
                        description: "System performance"
                    }
                ],
                announcements: [
                    "System update scheduled for tonight at 11 PM",
                    "New admin features available in settings",
                    "Backup completed successfully"
                ]
            },
            admin: {
                widgets: [
                    {
                        title: "Active Users",
                        value: (data.users?.active || 0).toString(),
                        icon: <UsersIcon className="h-10 w-10 text-green-600 mr-4" />,
                        description: "Currently online"
                    },
                    {
                        title: "Low Stock Items",
                        value: (data.inventory?.lowStock || 0).toString(),
                        icon: <ChartBarIcon className="h-10 w-10 text-red-600 mr-4" />,
                        description: "Need restocking",
                        alert: (data.inventory?.lowStock || 0) > 0
                    }
                ],
                announcements: [
                    "New admin features available in settings",
                    "New feature release! Check out the updated dashboard",
                ]
            },
            cashier: {
                widgets: [
                    {
                        title: "My Sales Today",
                        value: `₱${formatNumber(data.userSales?.today || 0)}`,
                        icon: <CurrencyDollarIcon className="h-10 w-10 text-blue-600 mr-4" />,
                        description: "Your personal sales"
                    },
                    {
                        title: "My Transactions",
                        value: (data.userSales?.transactions || 0).toString(),
                        icon: <ShoppingCartIcon className="h-10 w-10 text-teal-600 mr-4" />,
                        description: "Your orders today"
                    }
                ],
                announcements: [
                    "New POS shortcut: Press F2 for quick product search",
                    "Weekend specials have been updated",
                    "Remember to clock in/out using the time tracker"
                ]
            }
        };

        return baseConfig[role] || {
            widgets: [],
            announcements: [
                "New feature release! Check out the updated dashboard",
                "Scheduled maintenance on Sunday, 10 PM - 12 AM"
            ]
        };
    };

    const handleTimeRangeChange = (range) => {
        setTimeRange(range);
    };

    if (loading && !dashboardData) {
        return (
            <Layout>
                <div className="p-6 bg-gray-100 min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </Layout>
        );
    }

    const roleConfig = getRoleConfig();
    const commonWidgets = getCommonWidgets();

    // Route based on tenant type and enabled modules
    if (!tenantLoading && tenantInfo) {
        const tenantType = tenantInfo?.tenantType;
        const enabledModules = tenantInfo?.enabledModules || {};
        const isAccountingTenant = isAccountingClient(tenantType);


        // Restaurant tenants get the POS sales dashboard
        if (tenantType === 'restaurant') {
            return <RestaurantDashboard />;
        }

        // If accounting tenant with tax enabled (but not accountingCore) -> TaxDashboard.
        // mrwd is excluded: it is accountingClient-scoped but hides tax_obligation
        // in the sidebar, so it belongs on the AccountingFirmDashboard below.
        if (isAccountingTenant && tenantType !== 'mrwd' && enabledModules.tax === true) {
            return <TaxDashboard />;
        }
        // If accounting tenant with accountingCore enabled -> AccountingFirmDashboard
        if (isAccountingTenant && enabledModules.accountingCore === true) {
            return <AccountingFirmDashboard />;
        }

    }

    return (
        <Layout pageTitle="Dashboard">
            <div className="p-6 bg-gray-100 min-h-screen">
                
                {/* Header with Time Range Selector */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                        <p className="text-gray-600">Welcome back! Here's your {role} overview.</p>
                    </div>
                    
                    {/* Time Range Selector */}
                    <div className="flex space-x-2 mt-4 lg:mt-0">
                        {['today', 'week', 'month', 'year'].map((range) => (
                            <button
                                key={range}
                                onClick={() => handleTimeRangeChange(range)}
                                className={`px-3 py-2 rounded-md text-sm font-medium capitalize ${
                                    timeRange === range
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex justify-between items-center">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                            <span>{error}</span>
                        </div>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            ×
                        </button>
                    </div>
                )}

                {/* Data Not Available Warning */}
                {!loading && !dashboardData && !error && (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-6">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                            <span>No dashboard data available. Please check if the server is running.</span>
                        </div>
                    </div>
                )}

                {/* Announcements Section
                <div className="mb-8">
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <SpeakerWaveIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Announcements
                    </h3>
                    <div className="space-y-3">
                        {roleConfig.announcements.map((msg, index) => (
                            <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                <p className="text-gray-700 flex items-start">
                                    <span className="text-blue-500 mr-2">📢</span>
                                    {msg}
                                </p>
                            </div>
                        ))}
                    </div>
                </div> */}

                <div className="mt-8 mb-8">
                    <SalesChart />
                </div>

                {/* Stats Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {commonWidgets.map((widget, index) => (
                        <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center">
                                    {widget.icon}
                                    <div>
                                        <h4 className="text-2xl font-bold text-gray-900">{widget.value}</h4>
                                        <p className="text-gray-600 font-medium">{widget.title}</p>
                                        <p className="text-sm text-gray-500 mt-1">{widget.description}</p>
                                    </div>
                                </div>
                                {widget.trend && widget.trend.value > 0 && (
                                    <div className={`flex items-center text-sm ${
                                        widget.trend.isPositive ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {widget.trend.isPositive ? 
                                            <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> : 
                                            <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                                        }
                                        {widget.trend.value}%
                                    </div>
                                )}
                                {widget.alert && (
                                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                )}
                            </div>
                            {widget.previousValue && widget.previousValue !== "0" && widget.previousValue !== "₱0" && (
                                <p className="text-xs text-gray-400 mt-2">
                                    Previous: {widget.previousValue}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                
                {/* Debug: Show actual API response 
                {import.meta.env.NODE_ENV === 'development' && dashboardData && (
                    <div className="mt-8 p-4 bg-gray-800 text-white rounded-lg">
                        <h3 className="text-lg font-semibold mb-2">Debug - API Response:</h3>
                        <pre className="text-sm overflow-auto">
                            {JSON.stringify(dashboardData, null, 2)}
                        </pre>
                    </div>
                )}*/}
            </div>
        </Layout>
    );
};

export default ClientDashboardPage;